/**
 * Fiche séance n°10 — Formation 7h Permis B → 125 cc / L5e ("Formation 125")
 *
 * Source : notes manuscrites de moniteur ECSR (mai 2026) — pages WA0005, WA0007,
 * WA0008, WA0009, WA0011, WA0012, WA0013, WA0016, WA0017, WA0019 du cahier.
 *
 * Cadre légal : arrêté du 7 janvier 2019 (modifié) — formation obligatoire de 7h
 * pour les titulaires du permis B souhaitant conduire une motocyclette légère
 * (≤ 125 cm³, ≤ 11 kW, ≤ 0,1 kW/kg) ou un véhicule de catégorie L5e
 * (tricycle motorisé) — sauf détention du permis B avant le 1er mars 1980 ou
 * pratique attestée par l'assureur dans les 5 dernières années.
 *
 * Structure : 1h théorie (salle) + 3h plateau (hors circulation) + 3h circulation.
 */

export const formation7hB125Cours = {
  slug: 'formation-7h-b-125',
  number: 10,
  title: 'Formation 7h Permis B → 125 cc / L5e',
  subtitle:
    "Programme COMPLET de la formation 7h obligatoire pour les titulaires du permis B souhaitant conduire une motocyclette légère 125 cm³ ou un tricycle L5e : 1h théorie + 3h plateau + 3h circulation.",
  duration: '7h (1h salle + 3h plateau + 3h circulation)',
  level: 'Titulaires permis B (hors dispense)',
  format: 'Théorie salle (1h) + Plateau (3h) + Circulation (3h)',
  accent: '#0891b2',
  icon: 'Bike',

  phraseCle:
    "Tu sais conduire une voiture. Là, tu vas apprendre à conduire un véhicule qui ne tient debout que parce que tu roules, qui ne pèse pas 1,2 tonne mais qui se voit deux fois moins, et que la moitié des automobilistes ne regardent pas. C'est ce changement de perception qui se joue en 7 heures.",

  source: {
    label: 'Notes moniteur ECSR — Formation 125 (arrêté 7 janvier 2019 modifié)',
    pages: 'Cahier moniteur — 10 pages',
  },

  remc: {
    competences: [
      {
        code: 'C1',
        label: 'Maîtriser le maniement de la motocyclette à allure réduite et normale',
        souscompetences: [
          'C1.1 — Connaître et utiliser les commandes',
          'C1.2 — Maintenir l\'équilibre, démarrer, s\'arrêter',
          'C1.3 — Doser l\'accélération et le freinage',
          'C1.4 — Diriger la moto en utilisant le regard',
        ],
      },
      {
        code: 'C2',
        label: 'Appréhender la route et circuler dans des conditions normales',
        souscompetences: [
          'C2.1 — Rechercher la signalisation, les indices utiles et en tenir compte',
          'C2.2 — Positionner le véhicule sur la chaussée et choisir la voie',
          'C2.3 — Adapter l\'allure aux situations',
          'C2.4 — Détecter les indices utiles à un changement de direction',
        ],
      },
      {
        code: 'C3',
        label: 'Circuler dans des conditions difficiles et partager la route',
        souscompetences: [
          'C3.1 — Évaluer et maintenir les distances de sécurité',
          'C3.6 — Choisir et réaliser une trajectoire de sécurité',
          'C3.9 — Conduire en présence d\'usagers vulnérables (piétons, cyclistes, 2RM)',
        ],
      },
    ],
    matrice_gde:
      "Niveaux 2-3 — Maîtrise du véhicule (niveau 2) + contexte de la conduite (niveau 3, transition voiture → 2RM)",
    ppo: "Pédagogie Par Objectifs : verbes mesurables (citer, identifier, exécuter, anticiper).",
  },

  objectifs: {
    savoir:
      "À la fin de cette formation, TU SAURAS citer les principaux facteurs d'accident en moto (vitesse, alcool, équipement, freinage, slalom, évitement), tu sauras identifier la signalisation et les règles spécifiques aux 2-roues (inter-files, voies réservées), et tu pourras expliquer les conséquences du débridage et du défaut d'équipement.",
    savoirFaire:
      "À la fin de cette formation, TU SAURAS FAIRE en plateau les exercices imposés (béquillage, démarrage-arrêt, slalom, demi-tour, parcours en 8, freinage d'urgence, évitement) et en circulation tu seras capable d'adapter ta trajectoire et ton allure à toute situation (intersection, virage, giratoire, STOP, dépassement, distance de sécurité).",
    savoirEtre:
      "Au sortir de cette formation, TU ADOPTERAS une posture d'analyse permanente : avant ton départ (équipement, pneus), pendant ta conduite (anticipation, regard porté loin, communication avec les autres usagers), après ton trajet (auto-évaluation honnête). Tu intégreras que la 125 t'impose une humilité et une visibilité actives — tu n'es plus dans une voiture-cocon.",
  },

  prerequis: [
    'Permis B en cours de validité (obtenu après le 1er mars 1980)',
    "Ne pas être dispensé : pas de pratique 125 attestée par assureur dans les 5 dernières années",
    'EPI complets : casque ECE 22.06, gants CE EN 13594, blouson + pantalon CE, chaussures montantes',
    'Moto-école : motocyclette légère 125 cm³ homologuée (ou L5e selon le cas)',
    'Livret d\'apprentissage / fiche de suivi formation 7h',
  ],

  supports: [
    "Référentiel REMC + extrait arrêté du 7 janvier 2019",
    "Statistiques accidentologie 2RM ONISR 2024",
    "Fiche débridage cyclomoteur (R317-23 / 135 € amende / hors assurance)",
    "Plateau homologué (parcours lent, slalom, freinage, évitement, 8)",
    "125 cm³ moto-école + EPI complet",
    "Voiture suiveuse + intercom radio + chasubles",
    "Itinéraire de circulation balisé (ville + route + giratoire)",
  ],

  deroule: [
    {
      etape: 1,
      duree: '15 min',
      titre: 'Accueil & tour de table',
      role: 'Animateur',
      animation:
        "Bienvenue ! On va commencer par faire connaissance — à votre tour, dites-moi votre prénom, votre métier, pourquoi vous faites cette formation, ce qui vous motive (mobilité urbaine, plaisir, trajet domicile-travail), votre expérience moto antérieure (jamais, scooter 50, ancien permis A oublié…), et quel véhicule vous visez après aujourd'hui (125 standard, scooter 125 GT, tricycle). Partagez aussi UNE peur. On pose le contrat pédagogique : ce n'est PAS un examen, c'est une formation. Le programme : 7h (1h théorie + 3h plateau + 3h circulation).",
      questions_eleve: [
        "Qui peut me dire quelle est la principale différence ressentie entre une voiture et une 125 ?",
        "Quel scénario te fait le plus peur en pensant à demain matin sur la 125 ?",
        "Es-tu déjà tombé en scooter / vélo ? Raconte-nous.",
      ],
      attendu:
        "Climat de confiance instauré, contrat pédagogique posé, tu es rassuré : pas d'examen aujourd'hui.",
    },
    {
      etape: 2,
      duree: '30 min',
      titre: "Théorie — Présentation générale & spécificités 2RM",
      role: 'Apport théorique',
      animation:
        "Je vais vous montrer en quoi une 125 est radicalement différente d'une voiture. C'est un véhicule équilibré PAR le mouvement (chute à l'arrêt si tu poses pas le pied), 130-160 kg de masse, une accélération 2 à 3 fois plus vive qu'une voiture compacte, une visibilité réduite (carénage étroit, phares plus bas), et surtout : ton corps est exposé. Signalisation et règles spécifiques 2-roues : voies réservées bus si autorisé, inter-files (où l'expérimentation est autorisée), zones de stationnement spécifiques. Tes EPI : casque, gants, blouson, pantalon, chaussures montantes. Retiens cette règle simple : à moto, tu es INVISIBLE par défaut — ta survie passe par une visibilité ACTIVE.",
      questions_eleve: [
        "Combien pèse en moyenne une 125 ?",
        "Quelles sont les voies où l'inter-files est autorisée pour les 2RM ?",
        "Que se passe-t-il en cas de chute sans gants ?",
      ],
      attendu:
        "À la fin de cette étape, tu SAURAS citer 3 différences majeures voiture/125 (équilibre, accélération, visibilité) et 4 EPI obligatoires/recommandés.",
    },
    {
      etape: 3,
      duree: '40 min',
      titre: "Théorie — Accidentalité 2RM, vitesse & psychoactifs",
      role: 'Apport théorique',
      animation:
        "On regarde les chiffres ONISR 2024. Les motos représentent 23 % de la mortalité routière pour moins de 2 % du trafic motorisé : 720 tués sur l'année dont 123 cyclomotoristes. Je vais vous présenter 4 cas-types : (1) STOP/intersection non vue, (2) virage trop large, (3) ouverture de portière, (4) angle mort camion. Côté VITESSE : ton champ visuel passe de 180° à l'arrêt à 45° à 100 km/h et 30° à 120 km/h. Distance d'arrêt = réaction + freinage. Côté ALCOOL & SUBSTANCES : 0,5 g/L pour le permis B confirmé (0,2 g/L pour vous en jeune permis), cannabis 0 % toléré. Sanctions identiques (R234-1, R235-1).",
      questions_eleve: [
        "Quel pourcentage de la mortalité représentent les 2RM en France ?",
        "À 90 km/h, quel est ton champ visuel approximatif ?",
        "Quelle est la tolérance d'alcoolémie pour un permis B ?",
      ],
      attendu:
        "À la fin de cette étape, tu SAURAS citer 3 chiffres clés ONISR et 2 facteurs aggravants (vitesse + psychoactifs).",
    },
    {
      etape: 4,
      duree: '15 min',
      titre: "Théorie — Débridage & risques spécifiques",
      role: 'Apport théorique',
      animation:
        "Un point réglementaire essentiel. Un cyclo est conçu pour 45 km/h max — pas plus. Pourtant 1 cyclo accidenté sur 2 est débridé. Le débridage augmente le risque d'accident de +30 %. La sanction : 135 € d'amende, immobilisation possible, confiscation, et surtout : tu PERDS ton assurance en cas d'accident. La responsabilité civile peut couvrir mais le débridage sera systématiquement retenu contre toi. Pollution aussi : un 50 cm³ 2-temps peut être jusqu'à 1000× plus polluant en hydrocarbures qu'une voiture Euro 6 (étude IFSTTAR 2017). Et même sur ta 125 : ne dépasse jamais les puissances homologuées par le constructeur.",
      questions_eleve: [
        "Quelle est l'amende pour débridage de cyclomoteur ?",
        "Que se passe-t-il avec l'assurance en cas d'accident sur un cyclo débridé ?",
        "Est-il interdit de modifier une 125 (échappement plus libre, reprog) ?",
      ],
      attendu:
        "Tu prends conscience que toute modification non homologuée = perte d'assurance + responsabilité aggravée.",
    },
    {
      etape: 5,
      duree: '20 min',
      titre: "Module théorique — Comportements & savoir-être",
      role: 'Échange',
      animation:
        "On va échanger sur 7 axes du programme officiel (arrêté 07/01/2019). (1) Le respect de la règle. (2) La prise d'information et la communication. (3) La prise de conscience des risques. (4) L'influence des pressions extérieures (environnement social, copains, image). (5) Le partage de la route avec les usagers vulnérables (piétons, cyclistes, autres motards). (6) Le respect des autres usagers et l'attitude courtoise au guidon. (7) La conduite économique et respectueuse de l'environnement. Pour chaque axe, on prend un cas concret de votre vie de motard.",
      questions_eleve: [
        "Si un piéton traverse hors clous, quelle est ta réaction ?",
        "Tu es au feu rouge, le scooter à côté de toi te défie au départ. Comment te comportes-tu ?",
        "Comment as-tu envie d'être perçu par les autres usagers ?",
      ],
      attendu:
        "À la fin de cette étape, tu sauras VERBALISER un cas concret où tu as choisi (ou tu choisiras) la courtoisie / la sécurité plutôt que l'orgueil.",
    },
    {
      etape: 6,
      duree: '2h',
      titre: "Plateau — Maniabilité hors circulation",
      role: 'Pratique encadrée',
      animation:
        "On enchaîne au plateau dans cet ordre fixe. ① Béquillage / dé-béquillage (béquille centrale, attention aux obstacles). ② Démarrage-arrêt avec position de conduite : genoux fermés, tête levée, regard mobile, 4 doigts sur le levier. ③ Slaloms lents (cônes espacés à 4 pas). ④ Demi-tours (13 pas puis 4 pas). ⑤ Huit. ⑥ Huit avec exercice du cône (1 cône d'avance). ⑦ Freinage d'urgence à 50 km/h : 2 freins combinés, transfert de masse. ⑧ Évitement : contre-braquage 2 temps. Retiens cette règle d'or pour toute la séance : LA TÊTE TOURNE AVANT LE GUIDON.",
      questions_eleve: [
        "Où poses-tu ton regard pendant le slalom ?",
        "Quels doigts sur le levier de frein avant et pourquoi 4 et pas 2 en formation ?",
        "Comment tournes-tu la moto lentement sans pied à terre ?",
      ],
      attendu:
        "Tu deviens FLUIDE sur 80 % des passages, sans pied à terre intempestif, regard porté loin sur tous les exercices.",
    },
    {
      etape: 7,
      duree: '3h',
      titre: "Circulation — Audit de conduite réel",
      role: 'Pratique en suivi',
      animation:
        "Avant départ on cale tout : chasubles, test de l'intercom (je répète toujours mes instructions 2 fois), nos signaux (hochement de tête = OK, mouvement droite-gauche = problème, klaxon = je m'arrête). On passera par niveau (du moins à l'aise au plus à l'aise). Objectifs par séquence : (1) Giratoires — contrôles + allure + clignotant + anticipation. (2) Intersections — placement + allure + contrôles + piétons. (3) Trajectoires — allure + placement + anticipation + trajectoire de sécurité + visibilité. Chacun roulera minimum 30 minutes, puis 10 min de débrief individuel.",
      questions_eleve: [
        "Qu'as-tu ressenti dans le 1er giratoire ?",
        "Pourquoi ce contrôle visuel rétroviseur avant le clignotant ?",
        "Quelle distance de sécurité as-tu maintenue avec la voiture de devant ?",
      ],
      attendu:
        "À la fin de cette étape, tu SAURAS auto-évaluer 3 axes d'amélioration concrets et tu réalises tes contrôles dans le bon ordre (rétro → clignotant → contrôle direct).",
    },
    {
      etape: 8,
      duree: '20 min',
      titre: "Clôture & remise du certificat",
      role: 'Animateur',
      animation:
        "On termine en tour de table : chacun cite UN apprentissage majeur retenu aujourd'hui et UNE action pour demain. Je vous remets votre certificat de formation 7h (modèle CERFA réglementaire). Garde-le bien : il est OBLIGATOIRE pour conduire une 125 ou L5e avec ton permis B, à conserver avec le permis + carte grise. Pour rappel : la 125 reste interdite à un permis B obtenu avant le 1er mars 1980 sans cette formation s'il n'a pas pratiqué dans les 5 dernières années (attestation assureur).",
      questions_eleve: [
        "Quel est ton ressenti global après ces 7 heures ?",
        "Qu'est-ce que tu n'oseras PLUS faire après aujourd'hui ?",
        "Sur quoi vas-tu travailler en priorité sur tes prochains kilomètres ?",
      ],
      attendu:
        "Clôture positive, certificat délivré, tu repars avec un plan d'action et une attestation valable légalement.",
    },
  ],

  contenus: [
    {
      id: 'specificites-125',
      titre: 'Spécificités du 2RM 125 cm³ vs voiture',
      page_source: 'Notes moniteur — Formation 125 (théorie)',
      points_cles: [
        "Véhicule équilibré PAR le mouvement (chute à l'arrêt si pas de pied à terre)",
        "Masse 130-160 kg : pas anodine, demande un contrôle moteur fin",
        "Accélération 2 à 3 × supérieure à une voiture compacte de même budget",
        "Visibilité réduite (carénage étroit, surface frontale < 1 m²)",
        "Exposition totale du conducteur : aucun habitacle, aucun airbag, aucune ceinture",
        "Phénomène SMIDSY (Sorry Mate, I Didn't See You) : 70 % des collisions voiture/2RM",
        "Inter-files : autorisée uniquement dans départements expérimentaux (cf. arrêté en cours)",
      ],
      schema: "Schéma comparatif voiture vs 125 : masse, accélération, surface frontale",
    },
    {
      id: 'plateau-7-exercices',
      titre: 'Plateau — Les 7 exercices imposés',
      page_source: 'Notes moniteur — pages WA0011, WA0016',
      points_cles: [
        "① Béquillage / dé-béquillage (centrale, latérale, attention obstacles)",
        "② Démarrage-arrêt : position de conduite (genoux fermés, tête levée, regard mobile)",
        "③ Slaloms lents : 4 pas entre cônes",
        "④ Demi-tours : 13 pas puis 4 pas",
        "⑤ Huit (lemniscate, regard 1 cône d'avance)",
        "⑥ Huit avec exercice du cône (regard porté loin)",
        "⑦ Freinage d'urgence (2 freins combinés) + Évitement (contre-braquage 2 temps)",
      ],
      schema: "Plans plateau avec cônes — voir scans WA0011, WA0016",
    },
    {
      id: 'procedure-stop-intersection',
      titre: 'Procédure STOP / intersection / changement de direction',
      page_source: 'Notes moniteur — pages WA0012, WA0013',
      points_cles: [
        "1. Placement : où je me place pour tourner (1/3 chaussée selon direction)",
        "2. Coussin de sécurité : je me décale légèrement pour mieux voir",
        "3. Allure : adaptation continue selon visibilité (priorité droite = pied à terre psychologique)",
        "4. Contrôles avant clignotant : rétro extérieur + contrôle direct angle mort",
        "5. Clignotant : déclenché APRÈS les contrôles",
        "6. Distance de sécurité : 2 secondes mini en circulation fluide",
        "7. Regard porté vers la sortie souhaitée (anticipation 12 secondes)",
      ],
      schema: "Séquence COC : Contrôle-Observation-Clignotant",
    },
    {
      id: 'circulation-consignes',
      titre: 'Consignes circulation & signaux',
      page_source: 'Notes moniteur — pages WA0017, WA0019',
      points_cles: [
        "Système intercom : instructions toujours répétées au moins 2 fois",
        "Hochement de tête avant = OK, j'ai compris",
        "Mouvement tête droite-gauche = problème ou pas compris",
        "Klaxon de la voiture suiveuse = arrête-toi (intercom rompu)",
        "Si je ne te vois plus, je ralentis et je t'attends",
        "En cas de perte : rester sur l'axe principal et se garer visible",
        "Respect strict du Code de la route (VMA, distances, signalisation)",
      ],
    },
    {
      id: 'forces-ordre-respect',
      titre: "Posture ECSR — Respect des forces de l'ordre",
      page_source: 'Principe pédagogique ECSR transversal',
      points_cles: [
        "Les forces de l'ordre (gendarmerie nationale, police nationale, police municipale) sont en TÊTE de la hiérarchie des priorités routières — avant les feux, les panneaux, le marquage",
        "Ce ne sont pas des adversaires : ce sont les garants du cadre légal qui PROTÈGE le motard (visibilité, équipement, vitesse)",
        "Un contrôle d'éclairage = vérification que tu es visible la nuit, donc participation à ce qui te garde en vie",
        "Réflexe à l'arrêt : couper le moteur, retirer le casque si possible, mains visibles sur le guidon",
        "Présenter calmement : permis + carte grise + attestation d'assurance (et certificat de formation 7h pour la 125)",
        "Échange courtois → indulgence fréquente sur les détails ; échange agressif → aggravation systématique",
        "Outrage à agent (R434-2) : 7 500 € + 6 mois ; rébellion (R433-6) : 1 an + 15 000 €",
        "Message ECSR à transmettre : « L'irrespect des forces de l'ordre n'est JAMAIS une stratégie gagnante, ni légalement, ni statistiquement. »",
      ],
    },
  ],

  evaluation: {
    type: "QCM oral + débrief final (la Formation 7h n'a PAS d'examen, seulement un suivi)",
    duree: '15 min',
    questions: [
      {
        q: 'Le certificat de formation 7h Permis B → 125 est valable :',
        choix: [
          'Uniquement la première année',
          'À vie, sans condition de pratique',
          'À vie, mais doit être présenté avec le permis B et la carte grise',
          "5 ans, puis il faut refaire la formation",
        ],
        bonne: 2,
        explication:
          "Le certificat de formation 7h est valable à vie tant que le permis B reste valide. Il doit être conservé avec le permis et la carte grise du véhicule, et présenté en cas de contrôle.",
      },
      {
        q: "Quelle est la cylindrée maximale autorisée par cette formation 125 ?",
        choix: [
          '50 cm³',
          '125 cm³ ET ≤ 11 kW (15 ch) ET ≤ 0,1 kW/kg',
          '250 cm³',
          'Aucune limite tant que la puissance est ≤ 35 kW',
        ],
        bonne: 1,
        explication:
          "La formation 7h donne accès aux motocyclettes légères : ≤ 125 cm³, ≤ 11 kW (15 ch), rapport puissance/poids ≤ 0,1 kW/kg. ET aux véhicules L5e (tricycles motorisés ≤ 15 kW pour ceux destinés au transport de personnes).",
      },
      {
        q: "Dans un giratoire, dans quel ordre dois-tu enchaîner contrôles et clignotant ?",
        choix: [
          "Clignotant puis contrôle rétro",
          "Contrôle rétro + angle mort PUIS clignotant PUIS placement",
          "Placement puis clignotant",
          "Klaxon puis clignotant",
        ],
        bonne: 1,
        explication:
          "L'ordre correct est : Contrôle (rétro + angle mort si besoin) → Clignotant → Placement. Le clignotant annonce une décision DÉJÀ prise par tes yeux. Sans contrôle préalable, le clignotant est une fausse promesse.",
      },
      {
        q: "Le débridage d'un cyclomoteur expose-t-il à des sanctions ?",
        choix: [
          "Non, ça reste un petit ajustement personnel",
          "Oui : amende 135 €, immobilisation possible, et non-couverture par l'assurance",
          "Oui mais juste un avertissement",
          "Seulement en cas d'accident",
        ],
        bonne: 1,
        explication:
          "Le débridage est sanctionné par une amende de 135 €, possibilité d'immobilisation / confiscation, et surtout l'assurance NE COUVRE PAS les dommages en cas d'accident. 1 cyclo accidenté sur 2 est débridé et le risque d'accident est +30 %.",
      },
      {
        q: "À 90 km/h, le champ visuel d'un motard est environ :",
        choix: ['180°', '90°', '50°', '20°'],
        bonne: 2,
        explication:
          "Le champ visuel passe de 180° à l'arrêt à environ 50° à 90 km/h et descend à 30° à 120 km/h. C'est la « vision tunnel » : la conscience périphérique disparaît, d'où l'importance d'anticiper et de relever le regard.",
      },
    ],
    cas_pratique: {
      enonce:
        "Tu sors d'un STOP à droite sur une route 2×2 voies à 70 km/h. La visibilité à gauche est limitée par une haie. Décris ta procédure complète.",
      reponse_attendue:
        "1) Arrêt complet pieds posés (R415-6). 2) Coussin de sécurité : je m'avance légèrement pour dégager la vue. 3) Contrôles : gauche-droite-gauche (R234 général). 4) Anticipation : si vue obstruée, je m'avance encore (toujours arrêté) pour gagner du champ visuel sans m'engager. 5) Décision : seulement quand je VOIS suffisamment loin (≥ 10s d'écart avec véhicule arrivant). 6) Engagement : démarrage en 1ʳᵉ, accélération franche pour atteindre rapidement 50 km/h, regard porté vers ma trajectoire d'insertion, pas vers la voiture qui arrive.",
    },
  },

  references: [
    {
      titre: "Arrêté du 7 janvier 2019 modifié",
      detail:
        "Fixe le programme de la formation de 7 heures obligatoire pour les titulaires du permis B souhaitant conduire une motocyclette légère ≤ 125 cm³ ou un véhicule L5e. Définit : 1h théorie + 3h plateau + 3h circulation, certificat délivré en fin de formation.",
    },
    {
      titre: "Article R311-1 du Code de la route",
      detail:
        "Définit la catégorie L3e (motocyclette) et L5e (tricycle motorisé). Pour L3e légère : cylindrée ≤ 125 cm³, puissance ≤ 11 kW, rapport puissance/poids ≤ 0,1 kW/kg.",
    },
    {
      titre: "Article R221-4 du Code de la route",
      detail:
        "Conditions d'accès à la conduite des motocyclettes légères avec le permis B : obtention du permis B avant le 1er mars 1980, OU pratique attestée par l'assureur dans les 5 dernières années, OU suivi de la formation de 7 heures.",
    },
    {
      titre: "Article R317-23 du Code de la route",
      detail:
        "Sanctions pour débridage des cyclomoteurs et motocyclettes légères : 135 € d'amende, immobilisation possible, perte de la garantie d'assurance.",
    },
    {
      titre: "Article R234-1 du Code de la route",
      detail:
        "Conduite sous l'empire d'un état alcoolique. Seuil : 0,5 g/L (0,2 g/L jeune permis probatoire). Sanctions : 4 à 6 points, amende jusqu'à 4 500 €, suspension permis.",
    },
    {
      titre: "Article R235-1 du Code de la route",
      detail:
        "Conduite après usage de stupéfiants : tolérance ZÉRO, 6 points, 4 500 € amende, jusqu'à 2 ans de prison. Pas de seuil minimum, dépistage salivaire et confirmation sanguine.",
    },
    {
      titre: "Articles R313-3 à R313-7 du Code de la route",
      detail:
        "Obligations d'éclairage 2RM : feux de croisement allumés en permanence (R313-4), conditions d'utilisation des feux de route et signalisation latérale.",
    },
    {
      titre: "REMC — Référentiel pour l'Éducation à une Mobilité Citoyenne",
      detail:
        "Compétences C1 (maîtrise du véhicule), C2 (conditions normales), C3 (conditions difficiles). Niveaux 2-3 de la matrice GDE (maîtrise + contexte).",
    },
    {
      titre: "Données ONISR 2024 / Étude IFSTTAR 2017",
      detail:
        "720 tués 2RM en 2024 (dont 123 cyclomotoristes, +28 vs 2023). 23 % de la mortalité routière pour < 2 % du trafic. Cyclo 2-temps débridé jusqu'à 1000× plus polluant qu'Euro 6 (hydrocarbures).",
    },
  ],

  conseils_jury: [
    "Cette formation n'a PAS d'examen final — c'est un SUIVI pédagogique. Le certificat est délivré en fin de 7h, peu importe le « niveau » de l'élève.",
    "Le formateur DOIT remettre le certificat réglementaire (modèle CERFA) à conserver AVEC le permis B et la carte grise du véhicule.",
    "Beaucoup d'élèves arrivent en mode « j'ai mon permis B depuis 20 ans, je sais conduire ». Le tour de table initial doit DÉSACTIVER cette posture en mettant en évidence le changement de paradigme (équilibre, visibilité, exposition).",
    "En circulation : 30 min minimum par élève imposées par l'arrêté du 7 janvier 2019. Ne jamais raccourcir, même sous pression horaire.",
    "L'audit de conduite porte sur 4 axes : Perception (regard, indices) — Analyse (priorités, dangers) — Décision (choix tactique) — Action (geste technique). Cibler les 1-2 axes les plus faibles par élève.",
    "Insister sur la spécificité du dépassement en 2RM : la 125 accélère 2× plus vite qu'une voiture, l'écart se referme très vite — anticipation cruciale.",
    "Ne jamais minimiser le débridage : présenter les conséquences ASSURANCE et POLLUTION (chiffre IFSTTAR ×1000 marquant).",
  ],
};

export default formation7hB125Cours;
